/**
 * The availability strip of the booking form, as pure parts: where the car's other reservations, their turnaround gaps
 * and the proposed term sit in a window of days, and whether a start day is still allowed. The server decides which
 * reservations collide (`konflikt`) and what the gap is; nothing here judges availability.
 */

import type { Dostupnost } from './api';
import { assignLanes, weekdayOf } from './rezervace-helpers';

const DAY = 86_400_000;

/** Whole days from `from` to `d` (both `YYYY-MM-DD`; negative when `d` is earlier). */
export const dayIndex = (from: string, d: string): number =>
  Math.round((Date.parse(`${d.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / DAY);

/** A span of day columns, inclusive, clipped to the window. */
export interface Rozsah {
  from: number;
  to: number;
}

export interface BlokRezervace extends Rozsah {
  id: number;
  lane: number;
  konflikt: boolean;
  technicky: boolean;
  /** a workshop block: the car is not rentable (drawn red) */
  servis: boolean;
  poTerminu: boolean;
  /** The reservation continues past the window's end (open-ended while overdue). */
  otevreny: boolean;
  label: string;
  title: string;
  /** The turnaround gap before the pickup and after the return, as day columns (null where it has no days). */
  mezeraPred: Rozsah | null;
  mezeraPo: Rozsah | null;
}

/** A servis event of the car in the window (read-only; one that overlaps the asked term blocks a rental). */
export interface BlokServisu extends Rozsah {
  id: number;
  lane: number;
  konflikt: boolean;
  label: string;
  title: string;
}

export interface Den {
  date: string;
  /** Saturday / Sunday. */
  vikend: boolean;
}

export interface Rozlozeni {
  dny: Den[];
  /** Day columns that carry a label (the window can be a year long), with the text. */
  popisky: Array<{ i: number; text: string }>;
  bloky: BlokRezervace[];
  lanes: number;
  servis: BlokServisu[];
  servisLanes: number;
  navrh: Rozsah | null;
}

function clip(r: Rozsah, n: number): Rozsah | null {
  if (r.to < 0 || r.from > n - 1 || r.to < r.from) return null;
  return { from: Math.max(0, r.from), to: Math.min(n - 1, r.to) };
}

/** `2026-10-21` → `21. 10.`; the window's first day and every 1st of a month carry the month, the rest the day. */
function popisek(date: string, withMonth: boolean): string {
  const d = Number(date.slice(8));
  return withMonth ? `${d}. ${Number(date.slice(5, 7))}.` : String(d);
}

/** Lay out the server's answer for the drawn term (`od` / `do` = the term the answer was asked for). */
export function rozlozeni(data: Dostupnost, term: { od: string; do: string }): Rozlozeni {
  const n = dayIndex(data.okno_od, data.okno_do) + 1;
  const dny: Den[] = Array.from({ length: Math.max(n, 0) }, (_, i) => {
    const date = new Date(Date.parse(`${data.okno_od}T00:00:00Z`) + i * DAY).toISOString().slice(0, 10);
    return { date, vikend: weekdayOf(date) >= 5 };
  });
  const krok = Math.max(1, Math.ceil(n / 16));
  const popisky = dny
    .map((d, i) => ({ d, i }))
    .filter(({ d, i }) => i % krok === 0 || d.date.endsWith('-01'))
    .map(({ d, i }) => ({ i, text: popisek(d.date, i === 0 || d.date.endsWith('-01')) }));

  const placed = data.rezervace
    .map((r) => {
      const from = dayIndex(data.okno_od, r.od);
      const konecIdx = r.konec == null ? n - 1 : dayIndex(data.okno_od, r.konec);
      // a late return reaches past its scheduled end: draw to the day it occupies the car until
      const to = Math.max(konecIdx, dayIndex(data.okno_od, r.do));
      const span = clip({ from, to }, n);
      return span ? { r, span, from, to } : null;
    })
    .filter((x): x is NonNullable<typeof x> => x != null);
  const lanes = assignLanes(placed.map((p) => p.span));
  const bloky = placed.map(({ r, span, from, to }, k): BlokRezervace => {
    const technicky = r.typ === 'technicky';
    const servis = r.typ === 'servis';
    const label = servis ? 'Servis' : technicky ? 'Technická' : (r.zakaznik_nazev ?? '—');
    return {
      ...span,
      id: r.id,
      lane: lanes[k]!,
      konflikt: r.konflikt,
      technicky,
      servis,
      poTerminu: r.po_terminu,
      otevreny: r.konec == null,
      label,
      title: `${label} · ${r.od.slice(0, 10)} – ${r.do.slice(0, 10)}${r.po_terminu ? ' · po termínu, vozidlo je venku' : ''}`,
      mezeraPred: data.mezera_dni > 0 ? clip({ from: from - data.mezera_dni, to: from - 1 }, n) : null,
      mezeraPo: data.mezera_dni > 0 && r.konec != null ? clip({ from: to + 1, to: to + data.mezera_dni }, n) : null,
    };
  });
  const servisPlaced = (data.servis ?? [])
    .map((e) => ({ e, span: clip({ from: dayIndex(data.okno_od, e.od), to: dayIndex(data.okno_od, e.do) }, n) }))
    .filter((x): x is { e: Dostupnost['servis'][number]; span: Rozsah } => x.span != null);
  const servisLane = assignLanes(servisPlaced.map((p) => p.span));
  const servis = servisPlaced.map(({ e, span }, k): BlokServisu => ({
    ...span,
    id: e.id_udalosti,
    lane: servisLane[k]!,
    konflikt: e.konflikt,
    label: e.popis.split(' ')[0] === 'STK' ? 'STK' : 'Servis',
    title: e.popis,
  }));
  const navrh = term.do >= term.od ? clip({ from: dayIndex(data.okno_od, term.od), to: dayIndex(data.okno_od, term.do) }, n) : null;
  return { dny, popisky, bloky, lanes: Math.max(1, ...lanes.map((l) => l + 1)), servis, servisLanes: Math.max(1, ...servisLane.map((l) => l + 1)), navrh };
}

/**
 * Why a start day is refused, or null. A new reservation cannot start on a day that has passed (today is fine — a
 * rental is judged by calendar day); a change is judged only when it moves the start, since an existing rental is
 * allowed to have begun in the past. `puvodni` = the stored start of a reservation being changed.
 */
export function zacatekChyba(od: string, today: string, puvodni: string | null): string | null {
  const day = od.slice(0, 10);
  if (day.length < 10) return null;
  if (puvodni != null && day === puvodni.slice(0, 10)) return null;
  return day < today ? 'Začátek nesmí být v minulosti.' : null;
}

/**
 * The servis events a RENTAL of the asked term would run into — what disables the save (nobody may book through them). A
 * reservation that is being changed is judged only on what is new, like the server: an event that already overlapped its
 * stored term (`puvodni`, `YYYY-MM-DD HH:mm`) is no reason to refuse a note edit.
 */
export function servisPrekazky(data: Pick<Dostupnost, 'servis'>, puvodni: { od: string; do: string } | null): Dostupnost['servis'] {
  const prekryva = (a: { od: string; do: string }) => puvodni != null && a.od.slice(0, 16) < puvodni.do.slice(0, 16) && puvodni.od.slice(0, 16) < a.do.slice(0, 16);
  return (data.servis ?? []).filter((e) => e.konflikt && !prekryva(e));
}
